import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {HQ} from '../sportConfig';
import usePlayerDialog from '../usePlayerDialog';
import './TeamPortraitWarming.css';

const ENDPOINT = '/sportsim/api/admin/player-portraits/team';
const notify = () => window.dispatchEvent(new Event('sports-hq-portrait-changed'));

function DialogScrollLock() {
  // Native dialog owns focus; reuse installed-app scroll locking/restoration.
  usePlayerDialog();
  return null;
}

export default function TeamPortraitWarming({match, simulation, matchUrl = ''}) {
  const [allowed, setAllowed] = useState(false);
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState('home');
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef(null);
  const previousFocus = useRef(null);
  const active = useRef(true);
  const identity = `${simulation || ''}:${match?.id || ''}:${matchUrl}`;
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    fetch('/admin/check', {signal: controller.signal, cache: 'no-store'})
      .then(response => { if (!controller.signal.aborted) setAllowed(response.ok); }).catch(() => {});
    return () => controller.abort();
  }, []);
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  useEffect(() => { setOpen(false); setJob(null); setError(''); setStarting(false); }, [identity]);
  useEffect(() => {
    if (!allowed || !match?.home_team || !match?.away_team) return undefined;
    const show = event => { event.preventDefault(); setOpen(true); };
    document.addEventListener('sports-hq-admin-shortcut', show);
    return () => document.removeEventListener('sports-hq-admin-shortcut', show);
  }, [allowed, match?.home_team, match?.away_team]);
  useEffect(() => {
    if (!open) return undefined;
    previousFocus.current = document.activeElement;
    dialog.current.showModal();
    return () => previousFocus.current?.focus?.({preventScroll: true});
  }, [open]);
  useEffect(() => {
    if (!job?.id || !['pending', 'running'].includes(job.status)) return undefined;
    const controller = new AbortController();
    let timer;
    const poll = async () => {
      try {
        const response = await fetch(`${ENDPOINT}/${job.id}`, {signal: controller.signal, cache: 'no-store'});
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.detail === 'string' ? result.detail : 'Hämtningen kunde inte kontrolleras.');
        if (controller.signal.aborted) return;
        setJob(result); notify();
        if (['pending', 'running'].includes(result.status)) timer = setTimeout(poll, 1000);
      } catch (failure) { if (!controller.signal.aborted) { setError(failure instanceof TypeError ? 'Hämtningen kunde inte kontrolleras. Försök igen.' : failure.message); setJob(current => ({...current, status: 'failed'})); } }
    };
    timer = setTimeout(poll, 500);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [job?.id, job?.status]);
  useEffect(() => {
    if (job?.status !== 'done' || !job.queued) return undefined;
    // Hockey's existing guarded worker may finish after this batch. Refresh
    // only local overview reads while waiting; never request extra source jobs.
    let remaining = 15;
    const timer = setInterval(() => { notify(); if (--remaining <= 0) clearInterval(timer); }, 2000);
    return () => clearInterval(timer);
  }, [job?.id, job?.status, job?.queued]);
  const start = async () => {
    if (starting) return;
    const target = identity;
    setStarting(true); setError('');
    try {
      const response = await fetch(ENDPOINT, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({
        sport: HQ.sport, simulation: String(simulation || match?.competition_id || ''),
        match_id: Number(match?.id) || null, match_url: matchUrl, side,
      })});
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.detail === 'string' ? result.detail : 'Hämtningen kunde inte startas.');
      if (active.current && currentIdentity.current === target) { setJob(result); notify(); }
    } catch (failure) { if (active.current && currentIdentity.current === target) setError(failure instanceof TypeError ? 'Hämtningen kunde inte startas. Försök igen.' : failure.message); }
    finally { if (active.current && currentIdentity.current === target) setStarting(false); }
  };
  if (!allowed || !open) return null;
  const busy = starting || ['pending', 'running'].includes(job?.status);
  return createPortal(<dialog ref={dialog} className="team-portrait-dialog" aria-labelledby="team-portrait-title" onCancel={() => setOpen(false)}>
    <DialogScrollLock/>
    <h2 id="team-portrait-title">Hämta lagbilder</h2>
    {!job ? <>
      <label htmlFor="portrait-team">Lag</label>
      <select id="portrait-team" value={side} onChange={event => setSide(event.target.value)} disabled={starting}>
        <option value="home">{match.home_team}</option><option value="away">{match.away_team}</option>
      </select>
      <p>Hämta tillgängliga spelarporträtt för {side === 'home' ? match.home_team : match.away_team} och spara dem lokalt?</p>
      <p className="team-portrait-note">Aktuella bilder återanvänds. Tillfälliga bilder behålls.</p>
    </> : <>
      <p className="team-portrait-team">{job.team}</p>
      <div role="status" aria-live="polite">
        {busy ? <><progress max={job.total || 1} value={job.completed}/><p>{job.total ? `${job.completed} av ${job.total} spelare` : 'Kontrollerar truppen…'}</p></> : <p>{job.status === 'done' ? !job.total ? 'Inga spelare finns publicerade.' : job.failed ? 'Vissa bilder kunde inte hämtas.' : 'Hämtningen är klar.' : 'Hämtningen kunde inte slutföras.'}</p>}
        {job.completed > 0 && <p>{job.downloaded} hämtade · {job.cached} fanns redan · {job.missing} saknas{job.queued > 0 ? ` · ${job.queued} väntar på källan` : ''}{job.failed > 0 ? ` · ${job.failed} kunde inte hämtas` : ''}</p>}
      </div>
      {busy && <p className="team-portrait-note">Hämtningen fortsätter om du stänger rutan.</p>}
    </>}
    {(error || job?.error) && <p role="alert">{error || job.error}</p>}
    <div className="team-portrait-actions">
      <button type="button" onClick={() => setOpen(false)}>{job || starting ? 'Stäng' : 'Avbryt'}</button>
      {!job ? <button type="button" className="team-portrait-primary" disabled={starting} onClick={start}>{starting ? 'Startar…' : 'Hämta bilder'}</button>
        : !busy && <button type="button" onClick={() => { setJob(null); setError(''); }}>Välj lag</button>}
    </div>
  </dialog>, document.body);
}
